/**
 * Real-time notifications for new internal chat messages.
 * - Écoute les conversations Firestore où l'utilisateur est participant.
 * - Toast + son + badge onglet quand un message arrive d'un autre expéditeur.
 * - À monter une seule fois par shell (Layout / Dashboard).
 */
import { useEffect, useRef } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { toast } from 'sonner';
import { playMessageSound } from '@/utils/notification-sounds';

const T = {
  en: { newMessage: 'New message', from: 'from' },
  fr: { newMessage: 'Nouveau message', from: 'de' },
};

let originalTitle: string | null = null;
let unreadTotal = 0;
function bumpTabBadge(delta: number) {
  if (typeof document === 'undefined') return;
  if (originalTitle === null) originalTitle = document.title.replace(/^\(\d+\)\s*/, '');
  unreadTotal = Math.max(0, unreadTotal + delta);
  document.title = unreadTotal > 0 ? `(${unreadTotal}) ${originalTitle}` : originalTitle;
}

export function clearTabBadge() {
  unreadTotal = 0;
  if (originalTitle && typeof document !== 'undefined') document.title = originalTitle;
}

export function useMessageNotifications() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const t = T[language === 'fr' ? 'fr' : 'en'];
  const lastSeen = useRef<Map<string, number>>(new Map());
  const initialized = useRef(false);

  useEffect(() => {
    if (!user?.id) return;

    const q = query(
      collection(db, 'conversations'),
      where('participantIds', 'array-contains', user.uid)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        snap.docChanges().forEach((change) => {
          if (change.type !== 'modified' && change.type !== 'added') return;
          const data: any = change.doc.data();
          const lastMsg: string = data.lastMessage || '';
          const senderId: string | undefined =
            data.lastMessageSenderId || data.lastSenderId;
          const at =
            data.lastMessageAt?.toMillis?.() ??
            (data.lastMessageAt ? new Date(data.lastMessageAt).getTime() : 0);

          const prev = lastSeen.current.get(change.doc.id) ?? 0;
          lastSeen.current.set(change.doc.id, at);

          // Ignore initial hydration (avoid a burst of toasts on first mount)
          if (!initialized.current) return;
          if (!at || at <= prev) return;
          if (!senderId || senderId === user.id) return;

          const sender =
            data.participants?.find((p: any) => p.userId === senderId)?.userName ||
            data.participantName ||
            '';

          toast.message(`💬 ${t.newMessage}${sender ? ` ${t.from} ${sender}` : ''}`, {
            description: lastMsg.slice(0, 120),
            duration: 5000,
          });
          try {
            playMessageSound();
          } catch {}
          bumpTabBadge(1);
        });

        if (!initialized.current) {
          // After first snapshot we start reacting to changes
          initialized.current = true;
        }
      },
      (err) => console.error('[useMessageNotifications] listener error:', err)
    );

    // Clear tab badge when the tab regains focus
    const onFocus = () => clearTabBadge();
    window.addEventListener('focus', onFocus);

    return () => {
      unsub();
      window.removeEventListener('focus', onFocus);
      initialized.current = false;
      lastSeen.current.clear();
    };
  }, [user?.id, t.from, t.newMessage]);
}
